import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useState, useEffect, createContext, useContext } from "react";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";

// Pages
import Home from "./pages/Home";
import About from "./pages/About";
import Services from "./pages/Services";
import Contact from "./pages/Contact";
import Login from "./pages/Login";
import AuthCallback from "./pages/AuthCallback";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";

// Program Pages
import CareRevive from "./pages/programs/CareRevive";
import FoundationsFirst from "./pages/programs/FoundationsFirst";
import ConciergeFrontline from "./pages/programs/ConciergeFrontline";
import ProgramPayment from "./pages/programs/ProgramPayment";

// Service Pages
import LifeSciences from "./pages/LifeSciences";
import AdminServices from "./pages/AdminServices";
import WorkforceDevelopment from "./pages/WorkforceDevelopment";
import ComplianceCredentialing from "./pages/ComplianceCredentialing";
import EhrImplementation from "./pages/EhrImplementation";
import PatientExperience from "./pages/PatientExperience";
import ClinicOperations from "./pages/ClinicOperations";
import GrowthScalability from "./pages/GrowthScalability";
import EhrPartnership from "./pages/EhrPartnership";
import RcmPartnership from "./pages/RcmPartnership";
import Consultation from "./pages/Consultation";
import ConsultationLP from "./pages/ConsultationLP";
import MedicaidCredentialing from "./pages/MedicaidCredentialing";
import CredentialingQuickReview from "./pages/CredentialingQuickReview";
import HealthcareOperationalAudits from "./pages/HealthcareOperationalAudits";
import QuoteGenerator from "./pages/QuoteGenerator";

// Legal Pages
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";

// Public Payment Pages
import PublicPayment from "./pages/PublicPayment";
import PaymentSuccess from "./pages/PaymentSuccess";
import InvoiceLookup from "./pages/InvoiceLookup";

// Portal Pages
import ClientDashboard from "./pages/portal/ClientDashboard";
import ClientInvoices from "./pages/portal/ClientInvoices";
import ClientTimeTracking from "./pages/portal/ClientTimeTracking";
import ClientPaymentMethods from "./pages/portal/ClientPaymentMethods";
import ClientChat from "./pages/portal/ClientChat";
import ClientProfile from "./pages/portal/ClientProfile";
import ClientBooking from "./pages/portal/ClientBooking";
import ClientSettings from "./pages/portal/ClientSettings";
import ClientProjects from "./pages/portal/ClientMyProjects"; // Redirects old URL to new
import ClientMyProjects from "./pages/portal/ClientMyProjects";
import ClientSubscriptions from "./pages/portal/ClientSubscriptions";
import ClientCompany from "./pages/portal/ClientCompany";
import ClientUpdates from "./pages/portal/ClientUpdates";

// Admin Pages
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminClients from "./pages/admin/AdminClients";
import AdminClientDetail from "./pages/admin/AdminClientDetail";
import AdminApplications from "./pages/admin/AdminApplications";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminInvoices from "./pages/admin/AdminInvoices";
import AdminBlog from "./pages/admin/AdminBlog";
import AdminChat from "./pages/admin/AdminChat";
import AdminAnalytics from "./pages/admin/AdminAnalytics";
import AdminPreferences from "./pages/admin/AdminPreferences";
import AdminEmailTemplates from "./pages/admin/AdminEmailTemplates";
import AdminProjects from "./pages/admin/AdminProjects";
import AdminProjectDetail from "./pages/admin/AdminProjectDetail";
import AdminSubscriptions from "./pages/admin/AdminSubscriptions";
import AdminDiscountCodes from "./pages/admin/AdminDiscountCodes";
import AdminCompanies from "./pages/admin/AdminCompanies";
import AdminContactSubmissions from "./pages/admin/AdminContactSubmissions";
import AdminEmailLogs from "./pages/admin/AdminEmailLogs";

// Blog Pages
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";

// Referral Program Pages
import ReferralProgram from "./pages/ReferralProgram";
import ReferralApplicationForm from "./pages/ReferralApplicationForm";
import PartnerLogin from "./pages/PartnerLogin";
import ReferralPartnerDashboard from "./pages/portal/ReferralPartnerDashboard";
import ReferralPartnerSettings from "./pages/portal/ReferralPartnerSettings";
import ReferralPromoLibrary from "./pages/portal/ReferralPromoLibrary";
import AdminReferrals from "./pages/admin/AdminReferrals";
import AdminReferralAnalytics from "./pages/admin/AdminReferralAnalytics";
import AdminConsultantHours from "./pages/admin/AdminConsultantHours";
import AdminPaymentLinks from "./pages/admin/AdminPaymentLinks";

// BDP Pages
import BDPDashboard from "./pages/bdp/BDPDashboard";
import BDPPaymentLinks from "./pages/bdp/BDPPaymentLinks";
import BDPHours from "./pages/bdp/BDPHours";
import BDPCommissions from "./pages/bdp/BDPCommissions";
import BDPLeaderboard from "./pages/bdp/BDPLeaderboard";

// SDR Pages
import SDRDashboard from "./pages/sdr/SDRDashboard";
import SDRCalendar from "./pages/sdr/SDRCalendar";
import SDRHours from "./pages/sdr/SDRHours";
import SDRLeaderboard from "./pages/sdr/SDRLeaderboard";
import AdminSDR from "./pages/admin/AdminSDR";

// Components
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import MarketingTags from "./components/MarketingTags";
import ScrollToTop from "./components/ScrollToTop";
import HubSpotScript from "./components/HubSpotScript";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Auth Provider
const isTokenValid = (token) => {
  if (!token || typeof token !== "string") return false;
  // Basic JWT format check (3 dot-separated base64 segments)
  const parts = token.split(".");
  if (parts.length !== 3) return false;
  try {
    // Check token expiry from payload
    const payload = JSON.parse(atob(parts[1]));
    if (payload.exp && payload.exp * 1000 < Date.now()) return false;
    return true;
  } catch {
    return false;
  }
};

const getStoredToken = () => {
  try {
    const token = localStorage.getItem("token");
    if (token && isTokenValid(token)) return token;
    // Clean up invalid tokens
    if (token) localStorage.removeItem("token");
    return null;
  } catch {
    return null;
  }
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(getStoredToken);

  useEffect(() => {
    const checkAuth = async () => {
      const storedToken = getStoredToken();
      if (storedToken) {
        try {
          const response = await fetch(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${storedToken}` },
          });
          if (response.ok) {
            const userData = await response.json();
            setUser(userData);
            setToken(storedToken);
          } else {
            localStorage.removeItem("token");
            setToken(null);
          }
        } catch (error) {
          localStorage.removeItem("token");
          setToken(null);
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const login = (userData, authToken) => {
    setUser(userData);
    setToken(authToken);
    localStorage.setItem("token", authToken);
  };

  const logout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (error) {
    }
    setUser(null);
    setToken(null);
    localStorage.removeItem("token");
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout, API, isSuperAdmin: user?.role === "super_admin" }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children, requiredRole, superAdminOnly, clientOnly }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Client-only routes: redirect admins/consultants to their own portal
  if (clientOnly && ["admin", "consultant", "super_admin"].includes(user.role)) {
    return <Navigate to="/admin" replace />;
  }

  // Super admin only routes
  if (superAdminOnly && user.role !== "super_admin") {
    return <Navigate to="/admin" replace />;
  }

  // Admin routes (allow admin, consultant, super_admin)
  if (requiredRole === "admin" && !["admin", "consultant", "super_admin"].includes(user.role)) {
    // If a client tries to access admin routes, send them to their portal
    if (user.role === "client") {
      return <Navigate to="/portal" replace />;
    }
    return <Navigate to="/" replace />;
  }

  // Referral partner routes
  if (requiredRole === "referral_partner" && user.role !== "referral_partner") {
    return <Navigate to="/" replace />;
  }

  // Business development partner routes
  if (requiredRole === "business_dev" && user.role !== "business_dev") {
    return <Navigate to="/" replace />;
  }

  // SDR routes
  if (requiredRole === "sdr" && user.role !== "sdr") {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Layout wrapper for public pages
const PublicLayout = ({ children }) => {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
};

// App Router
function AppRouter() {
  const location = useLocation();

  // Check for session_id in URL fragment (OAuth callback)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Pages */}
      <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
      <Route path="/about" element={<PublicLayout><About /></PublicLayout>} />
      <Route path="/services" element={<PublicLayout><Services /></PublicLayout>} />
      <Route path="/contact" element={<PublicLayout><Contact /></PublicLayout>} />
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      
      {/* Program Pages */}
      <Route path="/programs/carerevive" element={<PublicLayout><CareRevive /></PublicLayout>} />
      <Route path="/programs/foundations-first" element={<PublicLayout><FoundationsFirst /></PublicLayout>} />
      <Route path="/programs/concierge-frontline" element={<PublicLayout><ConciergeFrontline /></PublicLayout>} />
      <Route path="/programs/pay" element={<ProgramPayment />} />
      
      {/* Service Pages */}
      <Route path="/life-sciences" element={<PublicLayout><LifeSciences /></PublicLayout>} />
      <Route path="/administrative-services" element={<PublicLayout><AdminServices /></PublicLayout>} />
      <Route path="/services/workforce-development" element={<PublicLayout><WorkforceDevelopment /></PublicLayout>} />
      <Route path="/services/compliance-credentialing" element={<PublicLayout><ComplianceCredentialing /></PublicLayout>} />
      <Route path="/services/ehr-implementation" element={<PublicLayout><EhrImplementation /></PublicLayout>} />
      <Route path="/services/patient-experience" element={<PublicLayout><PatientExperience /></PublicLayout>} />
      <Route path="/services/clinic-operations" element={<PublicLayout><ClinicOperations /></PublicLayout>} />
      <Route path="/services/growth-scalability" element={<PublicLayout><GrowthScalability /></PublicLayout>} />
      <Route path="/consultation" element={<PublicLayout><Consultation /></PublicLayout>} />
      <Route path="/employee-consultation" element={<ConsultationLP />} />
      <Route path="/medicaid-credentialing-services" element={<MedicaidCredentialing />} />
      <Route path="/credentialing-quick-review" element={<CredentialingQuickReview />} />
      <Route path="/quote" element={<PublicLayout><QuoteGenerator /></PublicLayout>} />
      
      {/* Legal Pages */}
      <Route path="/privacy" element={<PublicLayout><PrivacyPolicy /></PublicLayout>} />
      <Route path="/terms" element={<PublicLayout><TermsOfService /></PublicLayout>} />
      
      {/* Public Payment Pages (no layout - standalone) */}
      <Route path="/pay/:invoiceId" element={<PublicPayment />} />
      <Route path="/pay/:invoiceId/success" element={<PaymentSuccess />} />
      <Route path="/pay-invoice" element={<InvoiceLookup />} />
      
      {/* Vendor Partnership Pages */}
      <Route path="/ehr-partnership" element={<PublicLayout><EhrPartnership /></PublicLayout>} />
      <Route path="/rcm-partnerships" element={<PublicLayout><RcmPartnership /></PublicLayout>} />
      
      {/* Standalone Landing Pages (not in nav) */}
      <Route path="/healthcare-operational-audits" element={<HealthcareOperationalAudits />} />
      
      {/* Blog Pages */}
      <Route path="/blog" element={<PublicLayout><Blog /></PublicLayout>} />
      <Route path="/blog/:slug" element={<PublicLayout><BlogPost /></PublicLayout>} />
      
      {/* Referral Program Pages */}
      <Route path="/referral-program" element={<PublicLayout><ReferralProgram /></PublicLayout>} />
      <Route path="/referral-program/apply" element={<ReferralApplicationForm />} />
      <Route path="/partner/login" element={<PartnerLogin />} />
      
      {/* Client Portal */}
      <Route path="/portal" element={<ProtectedRoute clientOnly><ClientDashboard /></ProtectedRoute>} />
      <Route path="/portal/projects" element={<ProtectedRoute clientOnly><ClientProjects /></ProtectedRoute>} />
      <Route path="/portal/my-projects" element={<ProtectedRoute clientOnly><ClientMyProjects /></ProtectedRoute>} />
      <Route path="/portal/company" element={<ProtectedRoute clientOnly><ClientCompany /></ProtectedRoute>} />
      <Route path="/portal/subscriptions" element={<ProtectedRoute clientOnly><ClientSubscriptions /></ProtectedRoute>} />
      <Route path="/portal/invoices" element={<ProtectedRoute clientOnly><ClientInvoices /></ProtectedRoute>} />
      <Route path="/portal/time" element={<ProtectedRoute clientOnly><ClientTimeTracking /></ProtectedRoute>} />
      <Route path="/portal/payment-methods" element={<ProtectedRoute clientOnly><ClientPaymentMethods /></ProtectedRoute>} />
      <Route path="/portal/chat" element={<ProtectedRoute clientOnly><ClientChat /></ProtectedRoute>} />
      <Route path="/portal/chat/:partnerId" element={<ProtectedRoute clientOnly><ClientChat /></ProtectedRoute>} />
      <Route path="/portal/profile" element={<ProtectedRoute clientOnly><ClientProfile /></ProtectedRoute>} />
      <Route path="/portal/booking" element={<ProtectedRoute clientOnly><ClientBooking /></ProtectedRoute>} />
      <Route path="/portal/settings" element={<ProtectedRoute clientOnly><ClientSettings /></ProtectedRoute>} />
      <Route path="/portal/updates" element={<ProtectedRoute clientOnly><ClientUpdates /></ProtectedRoute>} />
      
      {/* Admin Portal */}
      <Route path="/admin" element={<ProtectedRoute requiredRole="admin"><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/clients" element={<ProtectedRoute requiredRole="admin"><AdminClients /></ProtectedRoute>} />
      <Route path="/admin/clients/:clientId" element={<ProtectedRoute requiredRole="admin"><AdminClientDetail /></ProtectedRoute>} />
      <Route path="/admin/chat" element={<ProtectedRoute requiredRole="admin"><AdminChat /></ProtectedRoute>} />
      <Route path="/admin/chat/:clientUserId" element={<ProtectedRoute requiredRole="admin"><AdminChat /></ProtectedRoute>} />
      <Route path="/admin/invoices" element={<ProtectedRoute requiredRole="admin" superAdminOnly><AdminInvoices /></ProtectedRoute>} />
      <Route path="/admin/users" element={<ProtectedRoute requiredRole="admin" superAdminOnly><AdminUsers /></ProtectedRoute>} />
      <Route path="/admin/applications" element={<ProtectedRoute requiredRole="admin" superAdminOnly><AdminApplications /></ProtectedRoute>} />
      <Route path="/admin/blog" element={<ProtectedRoute requiredRole="admin" superAdminOnly><AdminBlog /></ProtectedRoute>} />
      <Route path="/admin/email-templates" element={<ProtectedRoute requiredRole="admin" superAdminOnly><AdminEmailTemplates /></ProtectedRoute>} />
      <Route path="/admin/projects" element={<ProtectedRoute requiredRole="admin"><AdminProjects /></ProtectedRoute>} />
      <Route path="/admin/projects/:projectId" element={<ProtectedRoute requiredRole="admin"><AdminProjectDetail /></ProtectedRoute>} />
      <Route path="/admin/subscriptions" element={<ProtectedRoute requiredRole="admin" superAdminOnly><AdminSubscriptions /></ProtectedRoute>} />
      <Route path="/admin/discounts" element={<ProtectedRoute requiredRole="admin" superAdminOnly><AdminDiscountCodes /></ProtectedRoute>} />
      <Route path="/admin/companies" element={<ProtectedRoute requiredRole="admin"><AdminCompanies /></ProtectedRoute>} />
      <Route path="/admin/analytics" element={<ProtectedRoute requiredRole="admin" superAdminOnly><AdminAnalytics /></ProtectedRoute>} />
      <Route path="/admin/consultant-hours" element={<ProtectedRoute requiredRole="admin" superAdminOnly><AdminConsultantHours /></ProtectedRoute>} />
      <Route path="/admin/payment-links" element={<ProtectedRoute requiredRole="admin" superAdminOnly><AdminPaymentLinks /></ProtectedRoute>} />
      <Route path="/admin/sdr" element={<ProtectedRoute requiredRole="admin" superAdminOnly><AdminSDR /></ProtectedRoute>} />
      <Route path="/admin/settings" element={<Navigate to="/admin/preferences" replace />} />
      <Route path="/admin/preferences" element={<ProtectedRoute requiredRole="admin"><AdminPreferences /></ProtectedRoute>} />
      <Route path="/admin/referrals" element={<ProtectedRoute requiredRole="admin" superAdminOnly><AdminReferrals /></ProtectedRoute>} />
      <Route path="/admin/contacts" element={<ProtectedRoute requiredRole="admin" superAdminOnly><AdminContactSubmissions /></ProtectedRoute>} />
      <Route path="/admin/email-logs" element={<ProtectedRoute requiredRole="admin" superAdminOnly><AdminEmailLogs /></ProtectedRoute>} />
      <Route path="/admin/referral-analytics" element={<ProtectedRoute requiredRole="admin" superAdminOnly><AdminReferralAnalytics /></ProtectedRoute>} />
      
      {/* Referral Partner Portal */}
      <Route path="/partner" element={<ProtectedRoute requiredRole="referral_partner"><ReferralPartnerDashboard /></ProtectedRoute>} />
      <Route path="/partner/settings" element={<ProtectedRoute requiredRole="referral_partner"><ReferralPartnerSettings /></ProtectedRoute>} />
      <Route path="/partner/promo-library" element={<ProtectedRoute requiredRole="referral_partner"><ReferralPromoLibrary /></ProtectedRoute>} />
      
      {/* Business Development Partner Portal */}
      <Route path="/sales" element={<ProtectedRoute requiredRole="business_dev"><BDPDashboard /></ProtectedRoute>} />
      <Route path="/sales/payment-links" element={<ProtectedRoute requiredRole="business_dev"><BDPPaymentLinks /></ProtectedRoute>} />
      <Route path="/sales/commissions" element={<ProtectedRoute requiredRole="business_dev"><BDPCommissions /></ProtectedRoute>} />
      <Route path="/sales/hours" element={<ProtectedRoute requiredRole="business_dev"><BDPHours /></ProtectedRoute>} />
      <Route path="/sales/leaderboard" element={<ProtectedRoute requiredRole="business_dev"><BDPLeaderboard /></ProtectedRoute>} />
      
      {/* SDR Portal */}
      <Route path="/sdr" element={<ProtectedRoute requiredRole="sdr"><SDRDashboard /></ProtectedRoute>} />
      <Route path="/sdr/calendar" element={<ProtectedRoute requiredRole="sdr"><SDRCalendar /></ProtectedRoute>} />
      <Route path="/sdr/hours" element={<ProtectedRoute requiredRole="sdr"><SDRHours /></ProtectedRoute>} />
      <Route path="/sdr/leaderboard" element={<ProtectedRoute requiredRole="sdr"><SDRLeaderboard /></ProtectedRoute>} />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <ScrollToTop />
          <MarketingTags />
          <HubSpotScript />
          <AppRouter />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
